import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Layers, Phone, Clock, Target } from 'lucide-react';

const MultiCampaignAggregator = ({ reducedMotion }) => {
  const [loading, setLoading] = useState(true);
  const [aggregateData, setAggregateData] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchAggregateData();
  }, []);

  const fetchAggregateData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/reports/multi-campaign`);
      const data = await response.json();
      setAggregateData(data);
    } catch (error) {
      console.error('Failed to fetch aggregate data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="multi-campaign-aggregator-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Layers className="w-5 h-5 text-gold" />
          Multi-Campaign Aggregator
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Unified view across all active campaigns
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-800/30 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : aggregateData ? (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="w-4 h-4 text-gold" />
                  <p className="text-xs text-slate-400">Total Campaigns</p>
                </div>
                <p className="text-2xl font-bold text-slate-100" data-testid="total-campaigns">{aggregateData.total_campaigns}</p>
              </div>
              <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <p className="text-xs text-slate-400">Total Calls</p>
                </div>
                <p className="text-2xl font-bold text-slate-100" data-testid="total-calls">{aggregateData.total_calls?.toLocaleString() || 0}</p>
              </div>
              <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-ink-500" />
                  <p className="text-xs text-slate-400">Avg Conversion</p>
                </div>
                <p className="text-2xl font-bold text-slate-100" data-testid="avg-conversion">{((aggregateData.avg_conversion_rate || 0) * 100).toFixed(1)}%</p>
              </div>
              <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-ink-500" />
                  <p className="text-xs text-slate-400">Avg Duration</p>
                </div>
                <p className="text-2xl font-bold text-slate-100" data-testid="avg-duration">{aggregateData.avg_duration || 0}s</p>
              </div>
            </div>

            {/* Campaign Breakdown */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Campaign Performance</h4>
              <div className="space-y-2">
                {aggregateData.campaigns?.map((campaign, index) => (
                  <div
                    key={campaign.id}
                    className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/50 hover:bg-slate-800/50 transition-all duration-200"
                    style={{
                      animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
                    }}
                    data-testid={`campaign-row-${campaign.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-lg bg-gold/100/20 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-gold" />
                        </div>
                        <div>
                          <h5 className="font-medium text-slate-100" data-testid="campaign-name">{campaign.name}</h5>
                          <p className="text-xs text-slate-400">{campaign.calls} calls</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-slate-400">Conversion</p>
                          <p className="text-sm font-semibold text-slate-100" data-testid="conversion-rate">{(campaign.conversion_rate * 100).toFixed(1)}%</p>
                        </div>
                        <Badge
                          className={campaign.status === 'active' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}
                          data-testid="campaign-status"
                        >
                          {campaign.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-slate-400 text-center py-8">No campaign data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default MultiCampaignAggregator;